import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { MapPin, Mail, Send, Phone, Instagram, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = location.pathname.startsWith('/de') ? 'de' : 'en';

  const seoContent = {
    en: {
      title: 'Contact Podcast Studio Zurich | Book a Session | Werdstrasse 36',
      description: 'Contact Podcast Zurich for professional podcast production. Studio located at Werdstrasse 36, 8004 Zürich. Book a video podcast, webinar, or live stream session. Email: info@podcastzurich.com',
    },
    de: {
      title: 'Kontakt Podcast Studio Zürich | Session buchen | Werdstrasse 36',
      description: 'Kontaktieren Sie Podcast Zürich für professionelle Podcast-Produktion. Studio an der Werdstrasse 36, 8004 Zürich. Buchen Sie Video-Podcast, Webinar oder Live-Stream. E-Mail: info@podcastzurich.com',
    }
  };

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": seoContent[currentLang].title,
    "description": seoContent[currentLang].description,
    "url": `https://podcastzurich.com/${currentLang}/contact`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Podcast Zurich",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Werdstrasse 36",
        "addressLocality": "Zurich",
        "postalCode": "8004",
        "addressCountry": "CH"
      },
      "telephone": "+41772179528",
      "email": "info@podcastzurich.com"
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    serviceInterest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const content = {
    en: {
      title: 'Get In Touch',
      subtitle: 'Ready to start your podcast journey? Let\'s talk!',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        company: 'Company Name',
        phone: 'Phone Number',
        service: 'Service Interest',
        servicePlaceholder: 'e.g., Video Podcast, Webinar, etc.',
        message: 'Tell us about your project',
        messagePlaceholder: 'Share your vision, goals, and any specific requirements...',
        submit: 'Send Message',
        submitting: 'Sending...',
      },
      contact: {
        title: 'Contact Information',
        address: 'Our Studio',
        addressDetail: 'Werdstrasse 36, 8004 Zürich, Switzerland',
        email: 'Email Us',
        emailDetail: 'info@podcastzurich.com',
      },
      success: 'Message sent successfully! We\'ll get back to you soon.',
      error: 'Failed to send message. Please try again.',
      team: {
        title: 'Meet Our Team',
        subtitle: 'Your partners for your project',
        members: [
          {
            name: 'Yannik Odiet',
            role: 'Founder',
            bio: 'Yannik has been in media production since 2018 and founded Podcast Zurich because his friends were looking for a place to record podcasts. Yannik helps you with pre-production and supports you with everything technically possible.',
            image: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/m959ayxl_Yannik%20Odiet.jpg',
          },
          {
            name: 'Thomas Häder',
            role: 'Podcaster & Media Expert',
            bio: 'Thomas is producer and host of the podcasts "Kadermeeting" and "Drei Gänge". Audio and podcasts are his strength. He supports you with advice and action and gives professional tips with his experience.',
            image: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/ksq1aqci_Thomas%20Ha%CC%88der.jpg',
          },
          {
            name: 'Dominik Kuran',
            role: 'Editor',
            bio: 'Dominik\'s expertise is audio and video editing as well as creating 2D and 3D videos. He will bring your podcast to life and creates a captivating mix of sound and image with his creations.',
            image: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/j9da5hjp_Dominik%20Kuran.jpg',
          },
        ],
      },
    },
    de: {
      title: 'Kontaktieren Sie uns',
      subtitle: 'Bereit, Ihre Podcast-Reise zu beginnen? Lassen Sie uns sprechen!',
      form: {
        name: 'Vollständiger Name',
        email: 'E-Mail-Adresse',
        company: 'Firmenname',
        phone: 'Telefonnummer',
        service: 'Service-Interesse',
        servicePlaceholder: 'z.B. Video-Podcast, Webinar, etc.',
        message: 'Erzählen Sie uns von Ihrem Projekt',
        messagePlaceholder: 'Teilen Sie uns Ihre Vision, Ziele und spezifische Anforderungen mit...',
        submit: 'Nachricht senden',
        submitting: 'Wird gesendet...',
      },
      contact: {
        title: 'Kontaktinformationen',
        address: 'Unser Studio',
        addressDetail: 'Werdstrasse 36, 8004 Zürich, Schweiz',
        email: 'E-Mail',
        emailDetail: 'info@podcastzurich.com',
      },
      success: 'Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.',
      error: 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.',
      team: {
        title: 'Unser Team',
        subtitle: 'Ihre Partner für Ihr Projekt',
        members: [
          {
            name: 'Yannik Odiet',
            role: 'Gründer',
            bio: 'Yannik ist seit 2018 in der Medienproduktion und hat Podcast Zürich gegründet, weil seine Freunde nach einem Ort gesucht haben, wo sie Podcasts aufnehmen können. Yannik hilft dir bei der Vorproduktion und unterstützt dich bei allem, was technisch machbar ist.',
            image: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/m959ayxl_Yannik%20Odiet.jpg',
          },
          {
            name: 'Thomas Häder',
            role: 'Podcaster und Medienexperte',
            bio: 'Thomas ist Produzent und Host von den Podcasts "Kadermeeting" und "Drei Gänge". Ton und Podcasts sind seine Stärke. Er steht dir mit Rat und Tat zur Seite und gibt mit seiner Erfahrung professionelle Tipps.',
            image: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/ksq1aqci_Thomas%20Ha%CC%88der.jpg',
          },
          {
            name: 'Dominik Kuran',
            role: 'Editor',
            bio: 'Dominiks Expertise sind die Audio- und Videobearbeitung sowie die Erstellung von 2D- und 3D-Videos. Er wird deinen Podcast zum Leben erwecken und schafft mit seinen Kreationen einen fesselnden Mix aus Ton und Bild.',
            image: 'https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/j9da5hjp_Dominik%20Kuran.jpg',
          },
        ],
      },
    },
  };

  const t = content[currentLang];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to Formspree - sends email to info@podcastzurich.com
      const response = await axios.post('https://formspree.io/f/xaqyldoo', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        serviceInterest: formData.serviceInterest,
        message: formData.message,
        language: currentLang,
        _subject: `[Podcast Zürich] New Contact: ${formData.name}`,
      }, {
        headers: {
          'Accept': 'application/json',
        },
      });

      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        serviceInterest: '',
        message: '',
      });

      // Redirect to thank you page
      navigate(currentLang === 'en' ? '/en/thankyou' : '/de/danke');
      
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error(t.error, {
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        canonicalPath={`/${currentLang}/contact`}
        ogImage="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/re8fiacy_DSC05748.jpg"
        structuredData={contactStructuredData}
      />
      <main className="min-h-screen pt-32 pb-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200" role="main">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.title}</h1>
            <p className="text-xl text-gray-600">{t.subtitle}</p>
          </header>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">{t.form.name}</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">{t.form.email}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company">{t.form.company}</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t.form.phone}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="serviceInterest">{t.form.service}</Label>
                    <Input
                      id="serviceInterest"
                      name="serviceInterest"
                      value={formData.serviceInterest}
                      onChange={handleChange}
                      placeholder={t.form.servicePlaceholder}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">{t.form.message}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t.form.messagePlaceholder}
                      rows={6}
                      required
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#ffff31', color: '#000' }}
                    className="w-full font-semibold hover:opacity-90"
                  >
                    {isSubmitting ? t.form.submitting : t.form.submit}
                    {!isSubmitting && <Send className="ml-2 w-5 h-5" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t.contact.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-gray-100">
                    <MapPin className="w-6 h-6" style={{ color: '#6b7280' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t.contact.address}</h3>
                    <p className="text-gray-600 text-sm">{t.contact.addressDetail}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-gray-100">
                    <Mail className="w-6 h-6" style={{ color: '#6b7280' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t.contact.email}</h3>
                    <a href="mailto:info@podcastzurich.com" className="text-gray-600 text-sm hover:underline">
                      {t.contact.emailDetail}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-gray-100">
                    <Phone className="w-6 h-6" style={{ color: '#6b7280' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      {currentLang === 'en' ? 'Phone & WhatsApp' : 'Telefon & WhatsApp'}
                    </h3>
                    <a href="tel:+41772179528" className="text-gray-600 text-sm hover:underline block">
                      077/217 95 28
                    </a>
                    <a 
                      href="https://wa.me/41772179528" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 text-sm hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">
                  {currentLang === 'en' ? 'Follow Us' : 'Folgen Sie uns'}
                </h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/podcast_zurich/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Instagram className="w-6 h-6" style={{ color: '#6b7280' }} />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/podcast-zurich/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Linkedin className="w-6 h-6" style={{ color: '#6b7280' }} />
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 text-white border-gray-900">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4">
                  {currentLang === 'en' ? 'Book a Session' : 'Session buchen'}
                </h3>
                <p className="text-gray-300 mb-4">
                  {currentLang === 'en'
                    ? 'Ready to record? Schedule your podcast session directly with our calendar.'
                    : 'Bereit zur Aufnahme? Buchen Sie Ihre Podcast-Session direkt über unseren Kalender.'}
                </p>
                <Button
                  size="lg"
                  style={{ backgroundColor: '#ffff31', color: '#000' }}
                  className="w-full font-semibold hover:opacity-90"
                  onClick={() => window.open('https://koalendar.com/e/podcast-session', 'KoalendarBooking', 'width=700,height=800,scrollbars=yes,resizable=yes')}
                >
                  {currentLang === 'en' ? 'Open Calendar' : 'Kalender öffnen'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

        {/* Team Section */}
        <section className="max-w-7xl mx-auto mt-20" aria-labelledby="team-heading">
          <header className="text-center mb-16">
            <h2 id="team-heading" className="text-4xl md:text-5xl font-bold mb-4">{t.team.title}</h2>
            <p className="text-xl text-gray-600">{t.team.subtitle}</p>
          </header>
          <div className="grid md:grid-cols-3 gap-8" role="list" aria-label="Team members">
            {t.team.members.map((member, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow" role="listitem">
                <figure className="aspect-square overflow-hidden bg-gray-200">
                  <img
                    src={member.image}
                    alt={`${member.name} - ${member.role} at Podcast Zurich`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="400"
                  />
                </figure>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                  <p className="text-sm font-semibold text-gray-600 mb-4">{member.role}</p>
                  <p className="text-gray-700 leading-relaxed">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact;